import React from 'react';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { AreaHighlightItem } from './types';
import { AreaHighlightCard } from 'components/area-highlights/AreaHighlightCard';
import { AreaHighlightsCarouselWrapper } from 'components/area-highlights/styled';
import { detailPageCategorySelector } from 'store/state/selectors/analytics';
import { DetailPageEventCategory } from 'analytics/handlers/detailPageSectionHandlers';
import withEvents from 'analytics/withEvents';
import { setFeedbackPopupOpen as openFeedbackPopup } from 'store/state/app/actions';
import { ISendFeedbackCallbackProps } from 'utils/entities';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { BaseCarousel } from 'ds/components/carousels/base-carousel';
import { useTheme } from 'hooks/useTheme';
import { ScrollBox } from 'ds/components/carousels/scroll-box';
import { getSpacing, SpacingScale } from 'ds/theme/spacing';

interface AreaHighlightCarouselProps {
  areaHighlightCardList: AreaHighlightItem[];
  setActiveInsightId: (id: string) => void;
  activeCarouselSlide?: number;
  onSlideIndexChange?: (slideIndex: number) => void;
  setFeedbackPopupOpen: (prop: ISendFeedbackCallbackProps) => void;
  eventCategory: DetailPageEventCategory;
  source: 'good_to_know' | 'about_to_come';
  oldStyle?: boolean;
  isWhiteBg?: boolean;
}

export const AreaHighlightCarouselView: React.FC<AreaHighlightCarouselProps> = ({
  areaHighlightCardList,
  setActiveInsightId,
  onSlideIndexChange,
  activeCarouselSlide,
  setFeedbackPopupOpen,
  oldStyle,
  isWhiteBg,
  source,
}) => {
  const designRange = useScreenBreakpoint();
  const hasArrows = designRange > 2 && areaHighlightCardList.length > 2;
  const { colors } = useTheme();

  const cards = areaHighlightCardList.map(({ insightType, areaMainInfo, id }) => {
    return (
      <div key={id}>
        <AreaHighlightCard
          id={id}
          insightType={insightType}
          handleClick={setActiveInsightId}
          areaMainInfo={areaMainInfo}
          setFeedbackPopupOpen={setFeedbackPopupOpen}
        />
      </div>
    );
  });

  if (designRange < 3) {
    return (
      <ScrollBox
        cacheKey={`area-highlights-${source}`}
        autoWidth
        onSlideIndexChange={onSlideIndexChange}
        padding={`0 ${getSpacing(3 * designRange as SpacingScale)}`}
      >
        {cards}
      </ScrollBox>
    );
  }

  return (
    <AreaHighlightsCarouselWrapper oldStyle={oldStyle} isWhiteBg={isWhiteBg}>
      <BaseCarousel
        noPadding
        hideControls={!hasArrows}
        onSlideIndexChange={onSlideIndexChange}
        activeCarouselSlide={activeCarouselSlide}
        customGradientColor={isWhiteBg ? colors.neutrals.white : undefined}
      >
        {cards}
      </BaseCarousel>
    </AreaHighlightsCarouselWrapper>
  );
};

const mapStateToProps = (state: State) => ({
  eventCategory: detailPageCategorySelector(state),
});

const mapDispatchToProps = {
  setFeedbackPopupOpen: openFeedbackPopup,
};

const AreaHighlightsCarouselWrapperWithEvents = withEvents<AreaHighlightCarouselProps>((sendEvent, props) => ({
  setActiveInsightId: (id: string) => {
    const selectedInsight = props.areaHighlightCardList.find(i => i.id === id);
    if (selectedInsight) {
      sendEvent('insight_expand', 'insight', {
        event: {
          insight_index: props.areaHighlightCardList.findIndex(i => i.id === id),
          insight_index_total: props.areaHighlightCardList.length,
          source: `explore_${props.source}`,
        },
        insight: {
          insight_id: id,
          trade_off: {
            is_good_trade_off: selectedInsight.isGoodTradeoff,
            trade_off_value: selectedInsight.areaMainInfo,
          },
        },
      });
    }
  },
  onSlideIndexChange(this: { prevIndex: number }, slideIndex: number) {
    const direction = (this.prevIndex || 0) < slideIndex ? 'right' : 'left';
    sendEvent(`${props.eventCategory}_explore_scroll`, props.eventCategory, {
      event: {
        scroll_direction: direction,
        scroll_source: props.source,
      },
    });

    this.prevIndex = slideIndex;

  },
}))(AreaHighlightCarouselView);

export const AreaHighlightCarousel = connect(mapStateToProps, mapDispatchToProps)(AreaHighlightsCarouselWrapperWithEvents);
